import {LimitsQuota} from "@/api";
import {isUnlimited} from "@/tools";
import {Box, SxProps, Table, TableBody, TableCell, TableHead, TableRow, Theme} from "@mui/material";
import {defineMessage, FormattedMessage, MessageDescriptor} from "react-intl";

/** Displays a value or the infinity symbol if the value represents "unlimited" */
const UnlimitedOrValue: React.FC<{value: number}> = ({value}) => (isUnlimited(value) ? "∞" : value);

const translations = {
    allocated: defineMessage({
        id: "limitsSummary.quotaTable.allocated",
        defaultMessage: "A",
    }),
    used: defineMessage({
        id: "limitsSummary.quotaTable.used",
        defaultMessage: "U",
    }),
    systemLimit: defineMessage({
        id: "limitsSummary.quotaTable.systemLimit",
        defaultMessage: "S",
    }),
    legend: defineMessage({
        id: "limitsSummary.quotaTable.legend",
        defaultMessage: "U: Used, A: Allocated, S: System limit",
    }),
} as const;

const tableStyle: SxProps<Theme> = (theme) =>
    ({
        "& .MuiTableCell-root": {
            borderBottom: "none",
            padding: theme.spacing(1, 1.5),
            textAlign: "left",
        },

        // to blend in with other info, we need to get the actual data cells
        // to the second half of the screen, so the name column must take 50%
        // and the rest share the other 50%
        "& td:nth-of-type(1), & th:nth-of-type(1)": {
            width: "50%",
        },

        // we set the used and allocated columns to 0% width so they
        // only take the space they need
        "& th:nth-of-type(2), & th:nth-of-type(3)": {
            width: "0%",
        },

        // adjusted the padding for used column so it lines up better with the other
        // info components
        "& th:nth-of-type(2), & td:nth-of-type(2)": {
            paddingLeft: theme.spacing(0.75),
        },

        // add right borders to used and allocated columns so the data part of the table
        // is visually separated better
        "& th:nth-of-type(2), & td:nth-of-type(2), & th:nth-of-type(3), & td:nth-of-type(3)": {
            borderRight: `1px solid ${theme.palette.divider}`,
        },
    }) as const;

const styles: Record<string, SxProps<Theme>> = {
    container: {
        width: "100%",
        display: "flex",
        flexDirection: "column",
    } as const,
    legend: (theme) =>
        ({
            marginTop: theme.spacing(1),
            fontSize: "0.875rem",
            fontStyle: "italic",
            color: theme.palette.text.secondary,
        }) as const,
} as const;

type Item = LimitsQuota & {
    label: MessageDescriptor;
};

type QuotaTableProps = {
    items: Item[];
};

const QuotaTable: React.FC<QuotaTableProps> = ({items}) => {
    return (
        <Box sx={styles.container}>
            <Table sx={tableStyle} size="small">
                <TableHead>
                    <TableRow>
                        <TableCell>
                            <FormattedMessage id="name" defaultMessage="Name" />
                        </TableCell>
                        <TableCell>
                            <FormattedMessage {...translations.used} />
                        </TableCell>
                        <TableCell>
                            <FormattedMessage {...translations.allocated} />
                        </TableCell>
                        <TableCell>
                            <FormattedMessage {...translations.systemLimit} />
                        </TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {items.map((item) => (
                        <TableRow key={item.name}>
                            <TableCell>
                                <FormattedMessage {...item.label} />
                            </TableCell>
                            <TableCell>{item.used}</TableCell>
                            <TableCell>{item.allocated}</TableCell>
                            <TableCell>
                                <UnlimitedOrValue value={item.systemLimit} />
                            </TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
            <Box sx={styles.legend}>
                <FormattedMessage {...translations.legend} />
            </Box>
        </Box>
    );
};

export default QuotaTable;
